import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

const reportID = '050B407B-7499-4182-8F80-4A341822C69C'; // Placeholder for report ID if needed

const WSASTab = ({ reportId, api, onSubmit, onSave , isPublished}) => {
  const [responses, setResponses] = useState({});
  const [reAssess, setReAssess] = useState(null);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const scaleLabels = [
    { value: 0, label: 'Not at all', color: '#6bab57' },
    { value: 1, label: '', color: '#d8a0b4' },
    { value: 2, label: 'Slightly', color: '#d8a0b4' },
    { value: 3, label: '', color: '#d8a0b4' },
    { value: 4, label: 'Definitely', color: '#d8a0b4' },
    { value: 5, label: '', color: '#d8a0b4' },
    { value: 6, label: 'Markedly', color: '#d8a0b4' },
    { value: 7, label: '', color: '#c060a0' },
    { value: 8, label: 'Very Severely', color: '#c060a0' }
  ];

  const questions = [
    "Because of my [problem] my ability to work is impaired. '0' means 'not at all impaired' and '8' means very severely impaired to the point I can't work.",
    "Because of my [problem] my home management (cleaning, tidying, shopping, cooking, looking after home or children, paying bills) is impaired.",
    "Because of my [problem] my social leisure activities (with other people e.g. parties, bars, clubs, outings, visits, dating, home entertaining) are impaired.",
    "Because of my [problem], my private leisure activities (done alone, such as reading, gardening, collecting, sewing, walking alone) are impaired.",
    "Because of my [problem], my ability to form and maintain close relationships with others, including those I live with, is impaired."
  ];

  const fieldNames = [
    "wsas_answer_1",
    "wsas_answer_2",
    "wsas_answer_3",
    "wsas_answer_4",
    "wsas_answer_5"
  ];

  // Load existing responses from API
  useEffect(() => {
    const loadExistingResponses = async () => {
      try {
        setLoading(true);
        const effectiveReportId = reportId || reportID;
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/WSASAssessment`);

        if (response.ok) {
          const data = await response.json();

          // Convert field names back to question indexes
          const indexedResponses = {};
          fieldNames.forEach((fieldName, index) => {
            if (data.questions && data.questions[fieldName] !== undefined && data.questions[fieldName] !== null) {
              indexedResponses[index] = data.questions[fieldName];
            }
          });

          setResponses(indexedResponses);
          setReAssess(data.reAssess);

          // Mark all loaded responses as saved
          const savedStates = {};
          Object.keys(indexedResponses).forEach(index => {
            savedStates[index] = 'saved';
          });
          if (data.reAssess !== null && data.reAssess !== undefined) {
            savedStates['re_assess'] = 'saved';
          }
          setSavingStates(savedStates);
        }
      } catch (error) {
        console.error('Error loading existing responses:', error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadExistingResponses();
  }, [reportId]);

  // API call for saving individual response
  const saveResponse = async (questionIndex, value) => {
    if (isPublished) return;

    const fieldName = fieldNames[questionIndex];
    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/WSASAssessment/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: parseInt(value)
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'saved'
      }));

      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error(`Error saving response for question ${questionIndex + 1}:`, error);
      setSavingStates(prev => ({
        ...prev,
        [questionIndex]: 'error'
      }));
    }
  };

  // API call for saving re-assess field
  const saveReAssess = async (value) => {
    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/sections/WSASAssessment/fields/re_assess`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: value
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        're_assess': 'saved'
      }));

      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error('Error saving re-assess:', error);
      setSavingStates(prev => ({
        ...prev,
        're_assess': 'error'
      }));
    }
  };

  const handleResponseChange = (questionIndex, value) => {
    setResponses(prev => ({
      ...prev,
      [questionIndex]: parseInt(value)
    }));

    // Clear any existing timeout for this question
    if (saveTimeouts[questionIndex]) {
      clearTimeout(saveTimeouts[questionIndex]);
    }

    // Set saving state immediately
    setSavingStates(prev => ({
      ...prev,
      [questionIndex]: 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveResponse(questionIndex, value);
    }, 400);

    setSaveTimeouts(prev => ({
      ...prev,
      [questionIndex]: timeoutId
    }));
  };

  const handleReAssessChange = (value) => {
    setReAssess(value);

    if (saveTimeouts['re_assess']) {
      clearTimeout(saveTimeouts['re_assess']);
    }

    setSavingStates(prev => ({
      ...prev,
      're_assess': 'saving'
    }));

    const timeoutId = setTimeout(() => {
      saveReAssess(value);
    }, 400);

    setSaveTimeouts(prev => ({
      ...prev,
      're_assess': timeoutId
    }));
  };

  const calculateScore = () => {
    const completedItems = Object.keys(responses).length;
    if (completedItems !== questions.length) return null;
    return questions.reduce((sum, _, index) => sum + (responses[index] || 0), 0);
  };

  const getSeverityLevel = (score) => {
    if (score === null) return null;
    if (score <= 10) return 'Subclinical - Low functional impairment';
    if (score <= 20) return 'Moderate functional impairment';
    return 'Severe functional impairment';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const score = calculateScore();
    if (score === null) {
      alert('Please complete all questions.');
      return;
    }

    const result = {
      assessmentType: 'WSAS',
      responses,
      reAssess,
      score,
      severity: getSeverityLevel(score),
      completedAt: new Date().toISOString()
    };

    if (onSubmit) {
      await onSubmit(result);
    }
  };

  const completedCount = Object.keys(responses).length;
  const canSubmit = completedCount === questions.length;
  const currentScore = calculateScore();

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading existing responses...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="assessment-container">
        <div className="error-state">
          <p>Error loading responses: {loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="assessment-header">
        <h2>WSAS Assessment</h2>
        <p><strong>Work and Social Adjustment Scale</strong></p>
        <p><strong>Instructions:</strong> Please rate how much your problem impairs your ability to carry out the following activities.</p>
      </div>

      {/* Column headers matching old Angular layout */}
      <div className="scale-header sticky-header">
        <div className="scale-labels wsas-scale-header">
          <div className="scale-label">
            {/* Empty space above question column */}
          </div>
          {scaleLabels.map(scale => (
            <div key={scale.value} className="scale-label wsas-scale" style={{ backgroundColor: scale.color, color: 'white' }}>
              <span className="scale-text" style={{ color: 'white', fontWeight: 'bold' }}>{scale.label || '\u00A0'}</span>
              <span className="scale-value" style={{ color: 'white' }}>{scale.value}</span>
            </div>
          ))}
          <div className="scale-label save-status-header">
            <span className="scale-text">Status</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="assessment-form">
        <div className="questions-container wsas-grid">
          {questions.map((question, index) => (
            <fieldset key={index} className="question-fieldset">
              <legend className="question-legend">
                {index + 1}. {question}
              </legend>
              <div className="response-options">
                {scaleLabels.map(scale => (
                  <div key={scale.value} className="radio-container">
                    <input
                      type="radio"
                      id={`wsas_${index}_${scale.value}`}
                      name={`wsas_question_${index}`}
                      value={scale.value}
                      checked={responses[index] === scale.value}
                      onChange={(e) => handleResponseChange(index, e.target.value)}
                      className="response-radio"
                    />
                    <label
                      htmlFor={`wsas_${index}_${scale.value}`}
                      className="radio-label sr-only"
                    >
                      {scale.value}
                    </label>
                  </div>
                ))}
              </div>
              <div className="save-status-container">
                {savingStates[index] === 'saving' && (
                  <div className="save-status saving" aria-label="Saving response">
                    <div className="spinner"></div>
                  </div>
                )}
                {savingStates[index] === 'saved' && (
                  <div className="save-status saved" aria-label="Response saved">
                    <span className="checkmark">&#10003;</span>
                  </div>
                )}
                {savingStates[index] === 'error' && (
                  <div className="save-status error" aria-label="Save failed">
                    <span className="error-mark">&#9888;</span>
                  </div>
                )}
              </div>
            </fieldset>
          ))}
        </div>

        <div className="form-actions">
          <div className="completion-status">
            <div>Completed: {completedCount} / {questions.length}</div>
            {currentScore !== null && (
              <div className="score-display">
                <strong>Score: {currentScore}</strong>
                <div className="severity-level">{getSeverityLevel(currentScore)}</div>
              </div>
            )}
          </div>
        </div>
      </form>
      </fieldset>
    </div>
  );
};

export default WSASTab;
